/**
 * Rewrite absolute SFCC *instance-host* anchor links inside BM-authored content
 * HTML to same-origin relative paths.
 *
 * SCAPI serves MarkupText custom attributes (`c_seoAdditionalCopy`,
 * `c_module*FeatureContent`, `c_module2Text`, `c_plpPromotions`,
 * `c_crossPromoMessaging`, …) with content-link-picker links already resolved
 * to the raw instance host, e.g.
 *
 *   https://bfkw-dev.my.commercecloud.salesforce.com/s/FSASTORE/betterhelp.html
 *   https://bfkw-dev.my.commercecloud.salesforce.com/s/FSASTORE/health-condition/diabetes
 *   https://bfkw-dev.my.commercecloud.salesforce.com/on/demandware.store/Sites-FSASTORE-Site/default/Page-Show?cid=shedrx
 *
 * Rendered verbatim, a click navigates the shopper OFF the current host (devhec
 * dev, prod `www.*`, or a TPA partner subdomain like `bri-fsa.devhec.com`) to
 * that instance ("sandbox") host. (SFCC-11795.)
 *
 * We strip the instance origin so the link stays same-origin, and strip the
 * PWA-Kit/SCAPI `/s/<siteId>/` storefront prefix (the Workers storefront serves
 * those paths at root — `/betterhelp.html`, `/health-condition/diabetes`, …).
 * `/on/demandware.*` paths keep their path (the Worker's `proxyToSFRA` serves
 * them). This also lets `PrefetchBuilderAnchors` SPA-delegate the now-same-origin
 * links. Mirrors composable's `normalizeProductUrl` + `urlUtils.formatSfraHref`
 * origin-stripping.
 *
 * Surgical by design — only touched when BOTH hold:
 *   1. the attribute is `href` (never `src`: product images live on the same
 *      instance host and must stay absolute — see `salesforce-image-transform.ts`);
 *   2. the host is `*.commercecloud.salesforce.com` (the instance/API family).
 * Cross-site links (`hsastore.com` inside FSA copy), external links
 * (`biltlabs.com`), and bare-domain own-site links (`fsastore.com`) are left
 * untouched — they are intentional or a separate concern.
 */

// Capture: (1) `href="` opener incl. the leading whitespace + quote, (2) the
// instance URL, (3) the closing quote. Anchored on whitespace before `href` so
// it can't match inside another attribute's value.
const SFCC_INSTANCE_HREF =
  /(\shref\s*=\s*["'])(https?:\/\/[a-z0-9.-]*\.commercecloud\.salesforce\.com\/[^"']*)(["'])/gi;

// Leading `/s/<siteId>` storefront prefix (with or without a trailing path).
const SCAPI_STOREFRONT_PREFIX = /^\/s\/[^/]+(\/|$)/;

export function rewriteSfccContentLinks(html: string): string {
  // Cheap bail-out: nothing to do if the instance host isn't even present.
  if (!html || !html.includes("commercecloud.salesforce.com")) return html;

  return html.replace(SFCC_INSTANCE_HREF, (_match, open, url, close) => {
    try {
      const { pathname, search, hash } = new URL(url);
      // Strip `/s/<siteId>/` -> `/`. `/on/demandware.*` and other paths (no
      // `/s/` prefix) are left intact, just origin-stripped to same-origin.
      const path = pathname.replace(SCAPI_STOREFRONT_PREFIX, "/");
      return `${open}${path}${search}${hash}${close}`;
    } catch {
      // Unparseable URL — leave the anchor exactly as authored.
      return `${open}${url}${close}`;
    }
  });
}
